import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import logo from '@assets/logo_landing_page.svg';
import LoopIcon from '@mui/icons-material/Loop';
import { useSendMagicLinkEmail } from './hooks';
import { FormProvider, Form } from 'react-hook-form';
import { TextFormField } from '@components';

interface ISendMagicLinkEmailProps {
  setEmail: (email: string) => void;
}

export const SendMagicLinkEmail = ({ setEmail }: ISendMagicLinkEmailProps) => {
  const {
    onSubmit,
    handlePreviousStep,
    handleRefresh,
    loading,
    formMethods,
    svg,
  } = useSendMagicLinkEmail({ setEmail });

  const { control } = formMethods;

  return (
    <FormProvider {...formMethods}>
      <Form
        control={control}
        onSubmit={e => onSubmit(e.event)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '3.5rem',
          width: '28rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <img
            src={logo}
            style={{
              objectFit: 'cover',
              height: '131px',
              width: '132px',
            }}
            alt='logo'
          />
        </Box>
        <Paper
          elevation={8}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '2rem',
            paddingLeft: '42px',
            paddingBlock: '3rem',
            gap: '2rem',
            width: '100%',
            borderRadius: 0,
            overflowY: 'scroll',
            overflowX: 'hidden',
            maxHeight: '70vh',
            '&::-webkit-scrollbar': {
              width: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'transparent',
              borderRadius: '10px',
            },
            '&:hover::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&:focus': {
              outline: 'none',
            },
            outline: 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1rem',
              padding: 0,
            }}
          >
            <AccountCircleIcon sx={{ fontSize: '3rem' }} color='primary' />
            <Typography
              sx={{ fontWeight: '600', fontSize: '1.5rem', color: 'primary' }}
            >
              Sign in to continue
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <TextFormField
              label='Email'
              variant='outlined'
              type='email'
              name='email'
              helperText={
                'Use corporate email address e.g. @brigadehealth.com or @seniordoc.com'
              }
              required
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Box
                  dangerouslySetInnerHTML={{ __html: svg }}
                  sx={{ width: 328, height: 60 }}
                />
              </Box>
              <Box
                sx={{
                  width: '60px',
                  height: '60px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <IconButton
                  sx={{
                    width: '30px',
                    height: '30px',
                    display: 'grid',
                    padding: 0,
                    placeItems: 'center',
                    ':hover': {
                      cursor: 'pointer',
                      bgcolor: theme => theme.palette.grey[50],
                      borderRadius: '6px',
                    },
                  }}
                  loading={loading}
                  onClick={handleRefresh}
                >
                  <LoopIcon />
                </IconButton>
              </Box>
            </Box>

            <TextFormField
              label='Enter the code shown above'
              variant='outlined'
              type='text'
              name='captcha_answer'
              required
              slotProps={{ htmlInput: { maxLength: 5 } }}
            />
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}
          >
            <Button
              variant='contained'
              type='submit'
              color='secondary'
              sx={{
                color: 'white',
                padding: '0.5rem',
                fontWeight: '500',
                width: '100%',
              }}
              disabled={loading}
            >
              SEND A SIGN IN LINK
            </Button>
            <Button
              variant='outlined'
              disabled={loading}
              sx={{ padding: '0.5rem', fontWeight: '500', width: '100%' }}
              onClick={handlePreviousStep}
            >
              USE PASSWORD
            </Button>
          </Box>
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: theme => theme.palette.semiBlack.main,
              textAlign: 'center',
              width: '100%',
            }}
          >
            If you forgot your email address, please contact your system
            administrator.
          </Typography>
        </Paper>
      </Form>
    </FormProvider>
  );
};
